// services/checkoutAPI.js
import { generateRequestId } from 'utils/checkoutHelpers';
import { readErrorBody } from 'utils/paymentErrors';

/**
 * Creates an order via the Netlify function
 * @param {Object} orderData - Order data including cart and customer info
 * @param {Object} orderData.orderCart - Cart data
 * @param {Object} orderData.customerInfo - Customer information
 * @returns {Promise<Object>} Order response from the API
 */
export const createOrder = async (orderData) => {
  const response = await fetch('/.netlify/functions/createOrder', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-request-id': generateRequestId(),
    },
    body: JSON.stringify({
      orderCart: orderData.orderCart,
      customerInfo: orderData.customerInfo,
    }),
  });

  if (!response.ok) {
    const errorData = await readErrorBody(response, 'createOrder');

    throw new Error(
      errorData.error ||
        `Server error (${response.status}): Failed to create order`,
    );
  }

  const result = await response.json();

  if (!result.order?.id) {
    throw new Error('Invalid order response: missing order ID');
  }

  return result;
};

/**
 * Processes payment for an existing order
 * @param {Object} paymentData - Payment data
 * @param {string} paymentData.orderId - Order ID to pay for
 * @param {string} paymentData.cardToken - Payment card token
 * @param {string} paymentData.cardholderName - Name on the card
 * @param {Object} paymentData.customerInfo - Customer information
 * @returns {Promise<Object>} Payment response from the API
 */
export const processPayment = async (paymentData) => {
  const response = await fetch('/.netlify/functions/payOrder', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-request-id': generateRequestId(),
    },
    body: JSON.stringify({
      orderId: paymentData.orderId,
      cardToken: paymentData.cardToken,
      cardholderName: paymentData.cardholderName,
      customerInfo: paymentData.customerInfo,
    }),
  });

  if (!response.ok) {
    const errorData = await readErrorBody(response, 'payOrder');

    throw new Error(
      errorData.error ||
        `Payment failed (${response.status}): Please check your payment details and try again.`,
    );
  }

  const result = await response.json();

  if (!result.payment) {
    throw new Error('Invalid payment response');
  }

  return result;
};

/**
 * Deletes an existing order
 * @param {string} orderId - Order ID to delete
 * @returns {Promise<Object>} Delete response from the API
 */
export const deleteOrder = async (orderId) => {
  if (!orderId) {
    throw new Error('Order ID is required');
  }

  const response = await fetch('/.netlify/functions/deleteOrder', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-request-id': generateRequestId(),
    },
    body: JSON.stringify({
      orderId: orderId,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorData;

    try {
      errorData = JSON.parse(errorText);
    } catch {
      errorData = { error: errorText };
    }

    // Raw text is fine here, unlike createOrder/processPayment (#613): callers
    // only send deleteOrder errors to Sentry, never to the customer.
    // Keep both the server's summary and Clover's reason (#554).
    const summary = errorData.error || 'Failed to delete order';
    const reason = errorData.details ? `: ${errorData.details}` : '';
    throw new Error(`${summary} (${response.status})${reason}`);
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error('Invalid delete response');
  }

  return result;
};
